import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Award, CheckCircle, ArrowRight, ArrowUpRight, Phone, Mail,
  Stethoscope, Cpu, Wrench, Truck, Sprout, Languages, ChevronDown,
  MapPin, GraduationCap, FileCheck, Sparkles, Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta } from '@/hooks/useSEO';

/**
 * /countries/canada
 * ────────────────────────────────────────────────────────────────────────
 * Parent hub page consolidating the two Canada PR pathways — PR Visa
 * (Provincial Nominee Programs) and Express Entry (Federal Fast-Track).
 * Source of truth: /app/memory/CANADA_HUB_STRATEGY_2026.md
 * ~3,200 words rendered. 1 H1 · 10 H2 · 20+ H3 · 12 FAQs · 3 JSON-LD blocks.
 */

const PRIMARY_PHONE = '+91-99588-48998';
const PRIMARY_EMAIL = 'info@zafco.in';
const MEA_REG = 'B-0762/DEL/PER/1000+/5/9148/2015';
const PR_VISA_URL = '/countries/canada/canada-pr-visa';
const EXPRESS_ENTRY_URL = '/cms/canada-express-entry';

const COMPARISON_ROWS = [
  {
    feature: 'Best for',
    pr: 'Professionals seeking province-specific sponsorship — strong fit for healthcare, trades, construction, oil & gas, hospitality and applicants below the federal CRS cut-off',
    ee: 'Federally-processed candidates with a high CRS score who want the fastest published route to PR (6 months) with no province-specific tie',
  },
  {
    feature: 'Processing time',
    pr: '8–12 months end-to-end (PNP nomination + federal file)',
    ee: '6 months from ITA to COPR for most files',
  },
  {
    feature: 'Key streams',
    pr: 'Ontario (OINP), Saskatchewan (SINP), Alberta (AAIP), British Columbia (BC PNP), Manitoba (MPNP), Nova Scotia (NSNP) + 5 smaller programmes',
    ee: 'FSW (Federal Skilled Worker), CEC (Canadian Experience Class), FSTP (Federal Skilled Trades)',
  },
  {
    feature: 'CRS score required',
    pr: 'Variable — most PNPs invite at CRS 300–450 federally, then add +600 bonus points on nomination',
    ee: '520–540 for general draws; 475–510 for category-based draws; 680–750 post-PNP nomination',
  },
  {
    feature: 'Language requirement',
    pr: 'IELTS General — CLB 7 minimum, 6.0+ in all four abilities (most provinces)',
    ee: 'CLB 7 for FSW · CLB 5–7 for CEC · CLB 5 for FSTP',
  },
  {
    feature: 'Work experience',
    pr: '1+ year skilled experience aligned to provincial in-demand occupation list',
    ee: '1 year continuous skilled work (FSW) or Canadian work experience (CEC) or trade certification (FSTP)',
  },
  {
    feature: 'Provincial nomination',
    pr: 'Built into the process — applicants apply directly to a provincial stream',
    ee: 'Optional but powerful — +600 CRS points on nomination, virtually guarantees an ITA',
  },
];

const DECISION_ROWS = [
  ['Federal CRS 480+, IT / engineering / finance background', 'Express Entry (general or category-based draw)', 'Start Express Entry assessment', EXPRESS_ENTRY_URL],
  ['Federal CRS 380–470, willing to settle in a specific province', 'PR Visa via PNP (OINP, SINP, AAIP)', 'Start PNP nomination plan', PR_VISA_URL],
  ['Healthcare professional (nurse, allied health, lab tech)', 'PR Visa via SINP / NSNP or category-based Express Entry', 'Speak to a healthcare-stream counsellor', '/contact?source=canada-hub-healthcare'],
  ['Tradesperson (welder, electrician, plumber, HVAC, heavy-equipment)', 'PR Visa via Alberta AAIP or Federal Skilled Trades (FSTP)', 'Start trades-stream assessment', PR_VISA_URL],
  ['Spouse already in Canada on a work or study permit', 'Express Entry via CEC', 'Open spousal-route file', EXPRESS_ENTRY_URL],
];

const PROCESS_STEPS = [
  ['01', 'Eligibility Assessment', 'Free 30-minute consultation, CRS pre-score, occupation-list match, language-proficiency baseline.'],
  ['02', 'ECA & IELTS Preparation', 'Educational Credential Assessment filed through WES / IQAS / ICAS. IELTS General coaching where required.'],
  ['03', 'Pathway Strategy', 'Final recommendation: federal Express Entry vs PNP nomination (or both, in parallel, where eligible).'],
  ['04', 'Express Entry Profile / PNP Application', 'Profile creation in IRCC portal or provincial nomination application with full documentary support.'],
  ['05', 'Invitation to Apply / Provincial Nomination', 'Receive ITA or nomination certificate. Trigger 60-day federal-application clock.'],
  ['06', 'Document Compilation & e-APR', 'PCC, medicals, proof of funds (CAD 14,690 single 2026), reference letters, ECA, IELTS scores filed electronically.'],
  ['07', 'Biometrics & Final Decision', 'Biometrics at the Canadian VAC in Delhi. IRCC final decision in 6 months (Express Entry) or 8–12 months (PNP).'],
  ['08', 'Landing & Settlement', 'COPR issued, visa stamping, pre-departure briefing, landing-airport assistance, 90-day post-landing settlement support.'],
];

const ZAFCO_REASONS = [
  ['MEA-Licensed Recruitment Agency', `RC: ${MEA_REG}. Verifiable on the Ministry of External Affairs' eMigrate portal.`],
  ['15+ Years in International Mobility', 'Operating Canada files since 2009; through every major IRCC reform from FSW to category-based Express Entry.'],
  ['10,000+ Verified Placements', 'Across Canada, USA, UK, Australia, Germany, Saudi Arabia, the UAE and 40+ other destinations.'],
  ['98% File-Success Rate', 'On cases we accept after the initial eligibility assessment. We tell you honestly if your file is not currently competitive.'],
  ['Dedicated Canada Desk', 'In-house specialists on FSW, CEC, FSTP, OINP, SINP, AAIP, BC PNP, MPNP and NSNP. No outsourcing.'],
  ['Complete Document Support', 'ECA (WES / IQAS / ICAS), IELTS coaching, reference-letter drafting, proof-of-funds compilation, IRCC web-form completion.'],
  ['Provincial Nomination Expertise', 'Active mandates across Ontario, Saskatchewan, Alberta, BC, Manitoba and Nova Scotia. We know which streams are drawing in 2026.'],
  ['Post-Landing Settlement', '90-day support after landing: PR card pick-up, SIN, banking, housing referrals, school enrolment, healthcare-card registration.'],
  ['Transparent Milestone Fees', 'Fees tied to defined milestones (assessment → profile → ITA / nomination → e-APR → COPR). No upfront mega-fee, no surprise add-ons.'],
  ['Verified Reviews', '4.7★+ on Google and Facebook from past applicants. Real names, real timelines, verifiable on the platforms themselves.'],
];

const PNP_CARDS = [
  { icon: Building2, name: 'Ontario (OINP)', desc: 'Largest PNP by intake. Strong demand for Tech Stream (data scientists, software engineers, devops), Healthcare and Skilled Trades. Express Entry-aligned Human Capital Priorities stream remains the highest-volume option for federal CRS 460+ candidates.', path: '/skilled/canada/ontario-pnp' },
  { icon: Sprout, name: 'Saskatchewan (SINP)', desc: 'Specialist destination for healthcare and tradespeople. Occupation In-Demand and Express Entry sub-categories both active in 2026. Lower federal-CRS bar than Ontario; one of the fastest provincial nominations in Canada.', path: '/skilled/canada/saskatchewan-pnp' },
  { icon: Wrench, name: 'Alberta (AAIP)', desc: 'Strong for oil & gas, engineering, healthcare and skilled trades. Alberta Express Entry stream targets candidates with CRS 300+ and connections to Alberta.', path: '/skilled/canada/alberta-pnp' },
  { icon: Cpu, name: 'British Columbia (BC PNP)', desc: 'Best-in-class Tech Stream — explicitly designed for IT, life sciences, biotech and engineering. International Graduate stream is open to applicants already in BC on a study or work permit.', path: '/skilled/canada/british-columbia-pnp-program' },
  { icon: MapPin, name: 'Manitoba (MPNP)', desc: 'Active Skilled Worker Overseas and International Education streams. Manitoba\u2019s occupation-in-demand list is one of the broadest in Canada in 2026.', path: '/skilled/canada/manitoba-provincial-nomination-for-skilled-professionals' },
  { icon: Stethoscope, name: 'Nova Scotia (NSNP)', desc: 'Specialist healthcare recruitment — registered nurses, licensed practical nurses, allied-health professionals. Critical-Construction Worker stream open through 2026.', path: '/skilled/canada/nova-scotia-nominee-program-nsnp-your-connection-to-canadian-immigration' },
];

const UPDATES_2026 = [
  { icon: Stethoscope, label: 'Category-based draws active', body: 'Healthcare, STEM, Trades, Transport, Agriculture & Agri-food, French-language. Cut-offs run 30–60 CRS points below general draws.' },
  { icon: FileCheck, label: 'Proof-of-funds (single): CAD 14,690', body: 'Family of four: CAD 27,297. Update your bank statements accordingly.' },
  { icon: Sparkles, label: 'PR admissions target: 485,000', body: 'Slightly trimmed from 2025, with an increased 65% economic-class share.' },
  { icon: GraduationCap, label: 'PGWP field-of-study rule', body: 'Only programmes linked to long-term labour-market shortages remain PGWP-eligible. Affects students transitioning to CEC.' },
];

const FAQS = [
  { q: 'Which is faster — Canada PR Visa or Express Entry?',
    a: 'Express Entry is the faster route in 2026 — 6 months from ITA to COPR for most files. Canada PR Visa (via PNP) takes 8–12 months because it adds a provincial-nomination layer before the federal file. However, PNP routes are often the only realistic option for applicants with federal CRS scores below 520.' },
  { q: 'What is the minimum CRS score for Canada PR in 2026?',
    a: 'For Express Entry general draws, 520–540 has been the typical 2026 cut-off. Category-based draws (healthcare, STEM, trades, transport, agriculture, French) routinely invite at 475–510. With a PNP nomination, the +600 CRS boost brings effective scores to 680–750, virtually guaranteeing an ITA.' },
  { q: 'Do I need a job offer to apply for Canada PR?',
    a: 'No — neither Express Entry nor PR Visa via PNP requires a job offer for the headline streams (FSW, FSTP, OINP Human Capital Priorities, SINP Occupation In-Demand). A valid LMIA-backed job offer adds 50–200 CRS points in Express Entry and accelerates several PNP streams, but it is not mandatory.' },
  { q: 'How much proof of funds do I need for Canadian PR in 2026?',
    a: 'The IRCC 2026 threshold is CAD 14,690 for a single applicant and CAD 27,297 for a family of four, held in liquid form (savings, mutual funds, fixed deposits) and demonstrable for at least the preceding six months. Applicants with a valid Canadian job offer plus existing Canadian work authorisation are exempt from proof-of-funds.' },
  { q: 'What IELTS score do I need for Canada PR?',
    a: 'CLB 7 minimum for Federal Skilled Worker (Express Entry) and most PNP streams. In IELTS General band terms, CLB 7 maps to Listening 6.0, Reading 6.0, Writing 6.0, Speaking 6.0 — with most streams requiring 6.0+ in every ability (not just an overall 6.0 average). CEC allows CLB 5 for NOC TEER 2/3 roles. FSTP allows CLB 5 for speaking/listening and CLB 4 for reading/writing.' },
  { q: 'Can my spouse and children come with me on Canada PR?',
    a: 'Yes — Canada PR is family-inclusive. Your spouse or common-law partner and all dependent children (under 22, unmarried) are included on the same application. Accompanying spouses receive open work permits, children receive free K-12 public schooling, and the entire family is covered under provincial healthcare from the day they land.' },
  { q: 'How long until I can apply for Canadian citizenship?',
    a: 'You can apply for Canadian citizenship after 3 years of physical presence in Canada as a PR, calculated as 1,095 days within any 5-year window. You must also meet language and tax-filing requirements. Canada allows dual citizenship (note: India requires PIO/OCI conversion after Canadian citizenship is granted).' },
  { q: 'What is a Provincial Nominee Program (PNP) and how does it differ from Express Entry?',
    a: 'A PNP is a province-managed immigration stream allowing a province to nominate skilled workers it specifically wants. Each of Canada\u2019s 10 provinces + Yukon runs its own PNP with its own occupation list, scoring matrix and application portal. Express Entry is a federal pool that ranks all candidates by CRS score and issues ITAs to the highest scorers. The two systems interoperate: most PNPs have an Express Entry-aligned stream that adds the 600-point CRS boost.' },
  { q: 'Is an Educational Credential Assessment (ECA) mandatory?',
    a: 'Yes for Express Entry FSW and for most PNP applications where your highest qualification was earned outside Canada. ECA must be obtained from an IRCC-designated organisation — WES (World Education Services) is the most common for Indian applicants. Alternatives: IQAS (University of Alberta), ICAS (International Credential Assessment Service), CES (Comparative Education Service). Allow 4–8 weeks for completion.' },
  { q: 'Can I apply for Canadian PR if my CRS score is below 480?',
    a: 'Yes, but not via federal Express Entry general draws in most months. Below 480 you should pursue PNP nominations (OINP, SINP, AAIP, MPNP, NSNP all routinely invite at federal CRS 300–470 once the +600 PNP boost is added) or wait for a category-based Express Entry draw in your occupation if you fall into healthcare, STEM, trades, transport, agriculture or French-language priorities.' },
  { q: 'How much does Canadian PR cost for an Indian applicant in 2026?',
    a: 'IRCC government fees total roughly CAD 1,525 per principal applicant (application + RPRF + biometrics) plus CAD 1,365 for a spouse and CAD 260 per dependent child. On top of that budget for ECA (CAD 250–350), IELTS exam (~INR 17,000), medicals (~INR 7,500 per adult), police clearances (~INR 500 PCC) and VAC biometrics fee (~INR 6,000). Zafco\u2019s professional fees are quoted on a milestone basis after the eligibility assessment.' },
  { q: 'What occupations are in highest demand in Canada in 2026?',
    a: 'The 2026 category-based Express Entry draws and active PNP occupation lists converge on six demand clusters: (i) Healthcare — registered nurses, nurse practitioners, allied health, physicians; (ii) STEM — software engineers, data scientists, civil/mechanical/electrical engineers, cybersecurity; (iii) Skilled Trades — welders, electricians, plumbers, HVAC, heavy-equipment operators, industrial mechanics; (iv) Transport — long-haul truck drivers, transport managers; (v) Agriculture & Agri-food — butchers, food processors, agricultural managers; (vi) French-language proficiency — applicants with NCLC 7+ in French receive priority draws regardless of occupation.' },
];

const CATEGORY_DRAWS = [
  { icon: Stethoscope, name: 'Healthcare', body: 'Nurses, NPs, allied health, physicians.' },
  { icon: Cpu, name: 'STEM', body: 'Software, data, civil/mechanical/electrical engineers.' },
  { icon: Wrench, name: 'Skilled Trades', body: 'Welders, electricians, plumbers, HVAC, heavy-equipment.' },
  { icon: Truck, name: 'Transport', body: 'Long-haul truck drivers, transport managers.' },
  { icon: Sprout, name: 'Agriculture', body: 'Butchers, food processors, agricultural managers.' },
  { icon: Languages, name: 'French-language', body: 'NCLC 7+ priority draws regardless of occupation.' },
];

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid={`canada-hub-faq-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-5 flex items-start justify-between gap-4"
        aria-expanded={open}
      >
        <span className="font-semibold text-zafco-blue">{q}</span>
        <ChevronDown className={`w-5 h-5 text-amber-600 mt-1 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && <p className="text-gray-700 leading-relaxed px-5 pb-5 -mt-1">{a}</p>}
    </div>
  );
};

const CanadaHubPage = () => {
  usePageMeta(
    'Canada PR Visa & Express Entry 2026 from India | ZAFCO',
    'MEA-licensed Canada immigration agency in India. Compare Canada PR Visa & Express Entry — 6-month processing, CRS 520-540, IELTS CLB 7. 10,000+ placements.',
    {
      canonical: '/countries/canada',
      image: '/social-media/og-canada-immigration.jpg',
      imageAlt: 'Canada PR Visa and Express Entry pathways for Indian skilled professionals — ZAFCO MEA-approved',
    },
  );

  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Countries', item: 'https://zafco.in/countries/canada' },
        { '@type': 'ListItem', position: 3, name: 'Canada', item: 'https://zafco.in/countries/canada' },
      ],
    };
    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const service = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Canada Immigration Consultancy — PR Visa & Express Entry',
      description: 'MEA-licensed Canadian PR consulting for Indian skilled professionals — Express Entry profile creation, CRS optimisation, Provincial Nominee Program (PNP) applications, ECA & IELTS support.',
      serviceType: 'Canada Immigration Consultancy',
      provider: { '@id': 'https://zafco.in/#organization' },
      areaServed: [
        { '@type': 'Country', name: 'Canada' },
        { '@type': 'Country', name: 'India' },
      ],
      audience: { '@type': 'Audience', audienceType: 'Indian skilled professionals seeking Canadian permanent residency' },
      url: 'https://zafco.in/countries/canada',
    };
    const inject = (data, id) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-canada-hub', id);
      el.text = JSON.stringify(data).replace(/<\/(script)/gi, '<\\/$1');
      document.head.appendChild(el);
      return el;
    };
    const els = [inject(breadcrumb, 'breadcrumb'), inject(faq, 'faq'), inject(service, 'service')];
    return () => { els.forEach((el) => el?.remove()); };
  }, []);

  return (
    <div data-testid="canada-hub-page">
      <PageHeader
        title="Canada PR Visa & Express Entry for Indian Skilled Professionals — 2026 Pathways"
        subtitle="MEA-licensed Canadian immigration partner with 10,000+ verified placements across 50+ countries. Compare both pathways, check your eligibility and lock in a 6-month route to permanent residency."
        breadcrumbs={[
          { label: 'Countries', path: '#' },
          { label: 'Canada', path: '/countries/canada' },
        ]}
        keywordsTier={['MEA Approved', 'Express Entry', 'Provincial Nominee Programs']}
      />

      {/* Trust strip */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <Shield className="w-8 h-8 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-bold text-lg">Government of India Approved Recruitment Agency</p>
              <p className="text-green-100 text-sm">MEA Registration Certificate: {MEA_REG}</p>
            </div>
            <Award className="w-8 h-8 flex-shrink-0 hidden md:block" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Numeric trust stats */}
      <section className="py-10 bg-white border-b border-slate-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              ['15+', 'Years Experience'],
              ['10,000+', 'Verified Placements'],
              ['98%', 'Success Rate'],
              ['50+', 'Countries Served'],
            ].map(([n, l]) => (
              <div key={l} className="bg-slate-50 rounded-xl p-4 text-center border border-slate-200">
                <div className="text-2xl md:text-3xl font-bold text-zafco-blue">{n}</div>
                <div className="text-xs md:text-sm text-gray-600 mt-1">{l}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="canada-hub-cta-compare">
              <a href="#pathways">
                <ArrowRight className="w-4 h-4 mr-2" aria-hidden="true" /> Compare Pathways
              </a>
            </Button>
            <Button asChild variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50" data-testid="canada-hub-cta-counsellor">
              <Link to="/contact?source=canada-hub-hero">
                <Phone className="w-4 h-4 mr-2" aria-hidden="true" /> Speak to a Canada Counsellor
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white" aria-labelledby="canada-overview">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Canada Corridor · 2026</span>
          <h2 id="canada-overview" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            Why Canada is the most accessible PR destination for Indians in 2026
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Canada is the most accessible and rewarding immigration destination for Indian skilled professionals in 2026. The country has set itself an immigration target of <strong>485,000 new permanent residents in 2026</strong>, with <strong>65% of that intake reserved for economic-class applicants</strong> — exactly the pool that Indian engineers, healthcare workers, IT specialists, accountants, tradespeople and managers fall into. For applicants from India, that translates to two parallel state-recognised gateways: <strong>Canada Express Entry</strong> (the federal fast-track system) and the <strong>Canada PR Visa programme</strong> (delivered through the country&rsquo;s eleven Provincial Nominee Programs, or PNPs).
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            The headline reasons Indian skilled professionals choose Canada in 2026 are unchanged but stronger than ever:
          </p>
          <ul className="grid md:grid-cols-2 gap-3 mb-6">
            {[
              ['6-month processing on Express Entry', 'Fastest federally-managed PR route in any G7 country.'],
              ['No job offer required', 'For FSW or CEC candidates with a competitive CRS score.'],
              ['Spouse and dependents included', 'Open work permits for spouses, free public schooling for children.'],
              ['Pathway to citizenship in 3 years', 'After PR is granted, with dual citizenship allowed.'],
              ['Universal healthcare & free schooling', 'Across all ten provinces, from day of landing.'],
              ['Recognition of Indian qualifications', 'Via WES, IQAS or ICAS Educational Credential Assessment.'],
            ].map(([t, b]) => (
              <li key={t} className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" aria-hidden="true" />
                <div>
                  <div className="font-semibold text-zafco-blue">{t}</div>
                  <div className="text-sm text-gray-600 mt-0.5">{b}</div>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-gray-700 leading-relaxed">
            At <Link to="/" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">ZAFCO Human Resource Management</Link>, we have placed Indian professionals into Canadian Express Entry draws and PNP streams since 2009. Our credentials are matter-of-fact: an active <strong>MEA Recruitment Licence (RC: {MEA_REG})</strong>, <strong>10,000+ verified placements across 50+ countries</strong>, a <strong>98% file-success rate</strong> on cases we accept, and a <strong>dedicated Canada desk</strong> holding in-house expertise on ECA, IELTS preparation, CRS calculation and provincial nomination strategy.
          </p>
        </div>
      </section>

      {/* Two Pathways Comparison */}
      <section id="pathways" className="py-16 bg-slate-50 border-t border-slate-200" aria-labelledby="canada-pathways">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="canada-pathways" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Canada PR Visa vs Canada Express Entry — at a glance
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6 max-w-3xl">
            Two routes lead to a Canadian PR card. Both are state-recognised, both result in the same permanent-resident status, and both are open to Indian nationals in 2026. They differ on speed, score thresholds and which province ultimately processes the file.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm md:text-[15px]">
              <thead className="bg-zafco-blue text-white">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold w-1/5">Feature</th>
                  <th className="text-left px-4 py-3 font-semibold">Canada PR Visa (Provincial Skilled Migration)</th>
                  <th className="text-left px-4 py-3 font-semibold">Canada Express Entry (Federal Fast-Track)</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((r, idx) => (
                  <tr key={r.feature} className={idx % 2 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="px-4 py-3 font-semibold text-zafco-blue align-top">{r.feature}</td>
                    <td className="px-4 py-3 text-gray-700 align-top">{r.pr}</td>
                    <td className="px-4 py-3 text-gray-700 align-top">{r.ee}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <h3 className="font-bold text-zafco-blue mb-2">Use the PR Visa route if…</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                you are a healthcare professional (Saskatchewan and Nova Scotia are actively recruiting nurses and care workers), a tradesperson with provincial demand (Alberta, BC, Manitoba), an applicant with strong ties to a specific province, or your federal CRS score is below the 520 general-draw threshold.
              </p>
              <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90 mt-4" data-testid="canada-hub-cta-pr-visa">
                <Link to={PR_VISA_URL}>
                  Learn About Canada PR Visa <ArrowUpRight className="w-4 h-4 ml-1" aria-hidden="true" />
                </Link>
              </Button>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <h3 className="font-bold text-zafco-blue mb-2">Use Express Entry if…</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                you have a CRS score above 475 (especially in 2026 category-based draws: healthcare, STEM, trades, transport, agriculture, French), you want the fastest possible PR processing, or you already have Canadian work experience under the CEC stream.
              </p>
              <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90 mt-4" data-testid="canada-hub-cta-express-entry">
                <Link to={EXPRESS_ENTRY_URL}>
                  Learn About Express Entry <ArrowUpRight className="w-4 h-4 ml-1" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Decision Guide */}
      <section className="py-16 bg-white" aria-labelledby="canada-decision-guide">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="canada-decision-guide" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Quick decision guide — which pathway fits your profile?
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6 max-w-3xl">
            If you are still uncertain which pathway suits your profile, the matrix below reflects how our Canada desk typically routes incoming files. Each CTA opens the consultation form with the lead source tagged for our Canada desk to route the file to the correct specialist within one working day.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm md:text-[15px]">
              <thead className="bg-zafco-blue text-white">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Your situation</th>
                  <th className="text-left px-4 py-3 font-semibold">Recommended pathway</th>
                  <th className="text-left px-4 py-3 font-semibold">Next step</th>
                </tr>
              </thead>
              <tbody>
                {DECISION_ROWS.map(([situation, pathway, cta, href], idx) => (
                  <tr key={situation} className={idx % 2 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="px-4 py-3 text-gray-700 align-top">{situation}</td>
                    <td className="px-4 py-3 text-gray-700 align-top font-medium">{pathway}</td>
                    <td className="px-4 py-3 align-top">
                      <Link to={href} className="text-amber-700 font-semibold hover:underline inline-flex items-center gap-1" data-testid={`canada-hub-decision-${idx}`}>
                        {cta} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 bg-slate-50" aria-labelledby="canada-process">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 id="canada-process" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Our eight-stage Canadian immigration process
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            We deliver every Canadian file through an eight-stage process. Pathway-specific work-permit and documentation hand-offs happen at stages 4–6 — the rest of the framework is identical across PR Visa and Express Entry mandates.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {PROCESS_STEPS.map(([n, name, body]) => (
              <div key={n} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-amber-600 text-sm font-bold tracking-wider">{n}</div>
                <div className="font-semibold text-zafco-blue mt-1">{name}</div>
                <div className="text-xs text-gray-600 mt-1 leading-relaxed">{body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2026 Updates Callout */}
      <section className="py-16 bg-white" aria-labelledby="canada-2026-updates">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-l-4 border-amber-500 rounded-2xl p-6 md:p-8">
            <h2 id="canada-2026-updates" className="text-2xl md:text-3xl font-bold text-zafco-blue font-syne mb-2 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-amber-600" aria-hidden="true" /> What changed for Canadian immigration in 2026
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              {UPDATES_2026.map((u) => {
                const Icon = u.icon;
                return (
                  <div key={u.label} className="bg-white rounded-xl p-4 border border-amber-200/60">
                    <div className="flex items-start gap-3">
                      <div className="bg-amber-100 text-amber-700 p-2 rounded-lg flex-shrink-0">
                        <Icon className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="font-semibold text-zafco-blue text-sm">{u.label}</div>
                        <div className="text-xs text-gray-600 mt-1 leading-relaxed">{u.body}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
              {CATEGORY_DRAWS.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.name} className="bg-white rounded-lg p-3 border border-amber-200/60 flex items-start gap-2">
                    <Icon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <div>
                      <div className="text-xs font-semibold text-zafco-blue">{c.name}</div>
                      <div className="text-xs text-gray-600">{c.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Zafco */}
      <section className="py-16 bg-slate-50" aria-labelledby="why-zafco-canada">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="why-zafco-canada" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Why choose ZAFCO for your Canadian immigration file
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8 max-w-3xl">
            The 10 reasons Indian skilled professionals route their Canadian file through ZAFCO instead of going direct or through an unlicensed consultant.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {ZAFCO_REASONS.map(([t, b], idx) => (
              <motion.div
                key={t}
                className="flex items-start gap-3 bg-white p-5 rounded-xl border border-slate-200"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.03 }}
              >
                <div className="bg-zafco-blue/10 text-zafco-blue rounded-lg p-2 flex-shrink-0">
                  <CheckCircle className="w-5 h-5" aria-hidden="true" />
                </div>
                <div>
                  <div className="font-semibold text-zafco-blue">{idx + 1}. {t}</div>
                  <div className="text-sm text-gray-600 mt-1 leading-relaxed">{b}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Provincial Nominee Programs */}
      <section className="py-16 bg-white" aria-labelledby="canada-pnp">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="canada-pnp" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Canadian Provincial Nominee Programs (PNPs) we operate
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8 max-w-3xl">
            Canada operates <strong>eleven Provincial Nominee Programs</strong> in 2026 — ten provinces plus the Yukon Territory. Quebec runs its own parallel system (PEQ / PRTQ). The six PNPs our Canada desk most frequently routes Indian applicants through, with their 2026 commercial signal:
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {PNP_CARDS.map((p) => {
              const Icon = p.icon;
              return (
                <Link
                  key={p.name}
                  to={p.path}
                  className="bg-slate-50 rounded-2xl p-6 border border-slate-200 hover:border-amber-400 transition-colors block"
                  data-testid={`canada-hub-pnp-${p.name.split(' ')[0].toLowerCase()}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-amber-100 text-amber-700 p-2.5 rounded-xl flex-shrink-0">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-bold text-zafco-blue mb-1.5 flex items-center gap-2">
                        {p.name} <ArrowUpRight className="w-4 h-4 opacity-60" aria-hidden="true" />
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          <p className="text-sm text-gray-600 mt-6 max-w-3xl">
            Each PNP runs its own application portal, scoring system, occupation list and processing timeline. Choosing the right one for your profile is the single most consequential decision in a PNP file — and the one most often mishandled by unlicensed consultants. Speak to our Canada desk before lodging anywhere.
          </p>
        </div>
      </section>

      {/* Success story */}
      <section className="py-16 bg-slate-900 text-white" aria-labelledby="canada-success">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 id="canada-success" className="sr-only">Success Story</h2>
          <blockquote className="bg-slate-800/60 border border-slate-700/60 rounded-2xl p-8">
            <p className="text-lg text-slate-100 leading-relaxed italic">
              &ldquo;I came to ZAFCO with a federal CRS of 432 — well below the general-draw cut-off. Their Canada desk re-routed me through Saskatchewan&rsquo;s Occupation In-Demand stream, lifted my IELTS from CLB 6 to CLB 8 over 14 weeks, and rebuilt my ECA. Six months after lodging the SINP application I had a provincial nomination. Three months after that I had my Confirmation of Permanent Residency. My family landed in Saskatoon in August 2025.&rdquo;
            </p>
            <footer className="mt-5 text-amber-400 font-semibold">
              — Rohit Mehra, <span className="text-slate-300 font-normal">Registered Nurse, formerly Apollo Hospitals (Delhi), now Saskatchewan Health Authority. Deployed via Zafco Canada desk, 11-month total timeline.</span>
            </footer>
          </blockquote>
        </div>
      </section>

      {/* Final CTAs */}
      <section className="py-16 bg-gradient-to-br from-zafco-blue to-slate-900 text-white" aria-labelledby="canada-final-cta">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="canada-final-cta" className="text-3xl md:text-4xl font-bold font-syne mb-3">
            Take the next step on your Canadian PR journey
          </h2>
          <p className="text-slate-300 mb-8 max-w-3xl">
            Tell us your profile in 60 seconds — our Canada desk responds within one working day with a CRS pre-score, pathway recommendation and indicative timeline.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to="/contact?source=canada-hub-final-cta"
              className="bg-amber-500 text-slate-900 rounded-2xl p-6 font-semibold flex items-center justify-between hover:bg-amber-400 transition-colors"
              data-testid="canada-hub-final-cta-recommendation"
            >
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-700">Free pathway analysis</span>
                <span className="text-lg">Compare PR Visa vs Express Entry — Get My Recommendation</span>
              </span>
              <ArrowUpRight className="w-6 h-6" aria-hidden="true" />
            </Link>
            <a
              href={`tel:${PRIMARY_PHONE.replace(/[^+\d]/g, '')}`}
              className="bg-slate-800/60 border border-slate-700 text-white rounded-2xl p-6 font-semibold flex items-center justify-between hover:bg-slate-800 transition-colors"
              data-testid="canada-hub-final-cta-phone"
            >
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-400">Speak to our Canada desk</span>
                <span className="text-lg">{PRIMARY_PHONE}</span>
              </span>
              <Phone className="w-6 h-6 text-amber-400" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section className="py-16 bg-white" id="contact" aria-labelledby="canada-contact-form">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-8">
            <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Talk to our Canada desk</span>
            <h2 id="canada-contact-form" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
              Start your Canadian PR file
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Share your profile — qualification, work experience, IELTS band, target province (if any). Our Canada desk responds within one working day with a CRS pre-score and pathway recommendation.
            </p>
          </div>
          <ConsultationForm sourceTag="canada-hub" />
          <div className="text-center text-sm text-gray-500 mt-6">
            Prefer email? <a href={`mailto:${PRIMARY_EMAIL}?subject=Canada%20PR%20enquiry`} className="text-zafco-blue font-semibold hover:underline">
              <Mail className="w-4 h-4 inline mr-1" aria-hidden="true" />{PRIMARY_EMAIL}
            </a>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-slate-50" aria-labelledby="canada-faqs">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 id="canada-faqs" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Frequently asked questions about Canada PR
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl">
            Twelve high-intent questions covering processing times, CRS thresholds, language requirements, proof of funds and costs — the questions our Canada counsellors are asked every day.
          </p>
          <div className="space-y-3">
            {FAQS.map((f, idx) => (
              <FaqItem key={f.q} q={f.q} a={f.a} idx={idx} />
            ))}
          </div>
        </div>
      </section>

      <div className="text-center py-6 text-xs text-gray-500 bg-white border-t border-slate-200">
        Last reviewed: February 2026 · Reviewed by ZAFCO Senior Canada Counsellor
      </div>
    </div>
  );
};

export default CanadaHubPage;
